package macha.sh.kotlin.pages.blogguides

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import macha.sh.kotlin.SiteModifiers
import macha.sh.kotlin.SiteTypography
import macha.sh.kotlin.applyFont
import macha.sh.kotlin.components.layouts.PageLayoutData
import macha.sh.kotlin.components.widgets.MarkdownSection
import macha.sh.kotlin.pages.Articles
import macha.sh.kotlin.pages.encodeURIComponent
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Text

// Root-relative: this page lives at /blogguides/post, so a bare relative URL
// would resolve against /blogguides/ and miss the resources.
private const val MANIFEST_URL = "/text-content/blog-posts/articles.json"
private const val ARTICLE_RESOURCE_BASE = "/text-content/blog-posts/articles/"
private val FRONTMATTER = Regex("^---\\n[\\s\\S]*?\\n---\\n")

@InitRoute
fun initBlogPostPage(ctx: InitRouteContext) {
    ctx.data.add(
        PageLayoutData(
            title = "Article",
            description = "An article by Macha.",
            path = "/blogguides/post",
            indexable = false,
        )
    )
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun BlogPostPage() { PostStructure.Content() }

object PostStructure {
    private val Mods = SiteModifiers.Blog

    private fun requestedSlug(): String =
        (js("new URLSearchParams(window.location.search)").get("post") as? String) ?: ""

    @Composable
    fun Content() = Box(SiteModifiers.PrimaryPanel, contentAlignment = Alignment.TopCenter) {
        Column(modifier = Mods.mainColumn().padding(1.5.cssRem), horizontalAlignment = Alignment.CenterHorizontally) {
            val slug = remember { requestedSlug() }
            val articles = Articles.remember(MANIFEST_URL)
            val meta = articles?.firstOrNull { it.slug == slug }
            var bodyState by remember(slug) { mutableStateOf<String?>(null) }
            var errorState by remember(slug) { mutableStateOf<String?>(null) }

            LaunchedEffect(meta) {
                if (meta == null) return@LaunchedEffect
                document.title = meta.title
                try {
                    val response = window.fetch(ARTICLE_RESOURCE_BASE + encodeURIComponent(meta.file)).await()
                    if (response.ok) {
                        bodyState = FRONTMATTER.replace(response.text().await(), "")
                        errorState = null
                    } else {
                        bodyState = null
                        errorState = "Could not load article (HTTP ${response.status})."
                    }
                } catch (t: Throwable) {
                    errorState = "Error loading article: ${t.message}"
                    bodyState = null
                }
            }

            val body = bodyState
            val error = errorState
            when {
                slug.isEmpty() -> SpanText("No article requested.", modifier = Mods.descText())
                articles == null -> {} // manifest still loading
                meta == null -> SpanText("Article not found.", modifier = Mods.descText())
                error != null -> SpanText(error, modifier = Mods.descText())
                body == null -> {} // article body still loading
                else -> Column(modifier = Modifier.fillMaxWidth().padding(bottom = 2.cssRem)) {
                    H1 { Text(meta.title) }
                    SpanText(
                        meta.tags.joinToString(" · ").uppercase(),
                        modifier = Modifier.applyFont(SiteTypography.Subheading)
                    )
                    MarkdownSection(text = body, modifier = Modifier.textAlign(TextAlign.Start).lineHeight(1.6))
                }
            }
        }
    }
}
